import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { TransformedProduct } from "../types/productApiTypes";
import type { EnrichedAttributeTermTransformed } from "../types/productApiTypes"; // Use the transformed type

// Type for the attributes read from URL (Attribute Taxonomy -> Term Slug)
export interface UrlSelectedAttributes {
  [attributeTaxonomy: string]: string;
}

export interface UseSingleProductUrlSyncResult {
  urlAttributes: UrlSelectedAttributes;
  syncComplete: boolean;
}

/**
 * Reads attribute parameters from URL (attribute_pa_taxonomy=term_slug), validates them,
 * and returns validated attributes.
 */
export const useUrlToAttributes = (
  product: TransformedProduct | null | undefined
): { urlAttributes: UrlSelectedAttributes; syncComplete: boolean } => {
  const [searchParams] = useSearchParams();
  const [urlAttributes, setUrlAttributes] = useState<UrlSelectedAttributes>({});
  const [syncComplete, setSyncComplete] = useState<boolean>(false);

  // Memoize product attributes to avoid unnecessary effect runs
  const productAttributes = useMemo(() => {
    const attrs = product?.enrichedAttributes || [];
    return attrs;
  }, [product?.id, product?.enrichedAttributes]); // Depend on ID and the attributes themselves

  useEffect(() => {
    let attributesChanged = false;
    const newAttributesFromUrl: UrlSelectedAttributes = {};

    // --- Start Processing Logic ---
    // Only process if we actually have product attributes to validate against.
    if (product && productAttributes.length > 0) {
      productAttributes.forEach((attr) => {
        if (attr.hasVariations && attr.taxonomy) {
          const paramName = `attribute_${attr.taxonomy}`;
          if (searchParams.has(paramName)) {
            const valueSlug = searchParams.get(paramName);
            const isValidSlug = attr.terms.some(
              (term: EnrichedAttributeTermTransformed) =>
                term.slug === valueSlug
            );
            if (valueSlug && isValidSlug) {
              newAttributesFromUrl[attr.taxonomy] = valueSlug;
            } else {
              console.warn(
                `[URL Sync] useUrlToAttributes: Invalid or empty slug '${valueSlug}' for taxonomy '${attr.taxonomy}' in URL.`
              );
            }
          }
        }
      });

      // Check if the derived attributes are different from the current state
      const derivedString = JSON.stringify(newAttributesFromUrl);
      const currentString = JSON.stringify(urlAttributes);
      if (derivedString !== currentString) {
        attributesChanged = true;
      }
    } else if (Object.keys(urlAttributes).length > 0) {
      // If no product/attributes, but state still has values, clear it.
      attributesChanged = true; // Signal that state needs to become {}
    }
    // --- End Processing Logic ---

    // --- Update State Conditionally ---
    if (attributesChanged) {
      setUrlAttributes(newAttributesFromUrl); // Set to derived (potentially empty) object
    }

    // --- Mark Sync Complete ---
    // Mark sync as complete after the first effect run attempt.
    // This allows dependent hooks to proceed even if the product wasn't ready initially.
    if (!syncComplete) {
      setSyncComplete(true);
    }

    // Dependencies: We need to react to changes in searchParams and when product/productAttributes become available/change.
    // We include `urlAttributes` in the deps array ONLY for the comparison check to prevent unnecessary state updates causing loops.
  }, [searchParams, product, productAttributes, urlAttributes, syncComplete]); // Added product to deps

  return { urlAttributes, syncComplete };
};

/**
 * Updates URL query string based on selected attributes (taxonomy -> term slug map).
 */
export const useAttributesToUrl = (
  selectedAttributeSlugs: UrlSelectedAttributes, // Expects { 'pa_color': 'blue', ... }
  product: TransformedProduct | null | undefined,
  variationSelectionComplete: boolean // <-- Add parameter
): void => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoize attributes for stability
  const productAttributes = useMemo(() => {
    const attrs = product?.enrichedAttributes || [];
    return attrs;
  }, [product?.id, product?.enrichedAttributes]);

  useEffect(() => {
    // --- Add Condition ---
    // Only run the URL update logic if the initial variation selection is complete.
    // This prevents clearing the URL before the initial state is read from it.
    if (!variationSelectionComplete) {
      return;
    }
    // --- End Condition ---

    // Only run if we have attributes to potentially sync
    if (productAttributes.length === 0) {
      return;
    }

    const params = new URLSearchParams(searchParams);
    let changed = false;

    // Keep track of params we've processed from the state
    const processedParams = new Set<string>();

    // Set/Update params based on current selections
    Object.entries(selectedAttributeSlugs).forEach(
      ([taxonomySlug, termSlug]) => {
        // Validate if this taxonomy exists on the product
        const attrDef = productAttributes.find(
          (attr) => attr.taxonomy === taxonomySlug && attr.hasVariations
        );
        if (attrDef && termSlug) {
          const paramName = `attribute_${taxonomySlug}`;
          processedParams.add(paramName); // Mark as processed
          if (params.get(paramName) !== termSlug) {
            params.set(paramName, termSlug);
            changed = true;
          }
        } else {
          console.warn(
            `[URL Sync] useAttributesToUrl: Invalid taxonomy '${taxonomySlug}' or empty termSlug '${termSlug}' found in selectedAttributeSlugs.`
          );
        }
      }
    );

    // Remove params for attributes that are no longer selected or are invalid
    productAttributes.forEach((attr) => {
      if (attr.hasVariations && attr.taxonomy) {
        const paramName = `attribute_${attr.taxonomy}`;
        // If we didn't just set this parameter (it's not in selectedAttributeSlugs)
        // AND it exists in the current URL params, remove it.
        if (!processedParams.has(paramName) && params.has(paramName)) {
          params.delete(paramName);
          changed = true;
        }
      }
    });

    // Update URL only if changes occurred
    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [
    selectedAttributeSlugs,
    productAttributes,
    searchParams,
    setSearchParams,
    variationSelectionComplete, // <-- Add dependency
  ]);
};

/**
 * Combined hook for bidirectional URL-state synchronization using slugs.
 */
export const useSingleProductUrlSync = (
  selectedAttributeSlugs: UrlSelectedAttributes, // Expects { 'pa_color': 'blue', ... }
  product: TransformedProduct | null | undefined,
  variationSelectionComplete: boolean // <-- Add parameter
): UseSingleProductUrlSyncResult => {
  // Read from URL first
  const { urlAttributes, syncComplete } = useUrlToAttributes(product);

  // Write to URL based on state, only after initial selection is complete
  useAttributesToUrl(
    selectedAttributeSlugs,
    product,
    variationSelectionComplete
  ); // <-- Pass flag

  return { urlAttributes, syncComplete };
};
